// ResearchPanel.tsx
import React, { useState, useRef } from 'react';
import { Search, CheckCircle, Sparkles, BookOpen, Clock, Activity, XCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { ProgressBar } from '../ui/ProgressBar';
import { webhookService, apiCancelTokens } from '../../services/api';
import type { Brief, ResearchResult, ArticleTypeId } from '../../types';

interface ResearchPanelProps {
  brief?: Brief;
  subtopics?: string[];
  mainTopic?: string;
  onComplete: (results: ResearchResult[]) => void;
  setIsProcessing?: (isProcessing: boolean) => void;
  onBack?: () => void;
  sessionId?: string;
  articleType?: ArticleTypeId;
}

export const ResearchPanel: React.FC<ResearchPanelProps> = ({
  brief,
  subtopics,
  mainTopic,
  onComplete,
  setIsProcessing,
  onBack,
  sessionId,
  articleType = 'wikipedia',
}) => {
  const [isResearching, setIsResearching] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTopic, setCurrentTopic] = useState('');
  const [results, setResults] = useState<ResearchResult[]>([]);
  const [error, setError] = useState('');
  const [showStartButton, setShowStartButton] = useState(true);
  const [isCancelled, setIsCancelled] = useState(false);

  // Refs for tracking execution state
  const hasStartedRef = useRef(false);
  const isExecutingRef = useRef(false);
  const cancelledRef = useRef(false);

  // Handle cancel button click
  const handleCancel = () => {
    cancelledRef.current = true;
    setIsCancelled(true);
    apiCancelTokens.cancelResearch();
    setCurrentTopic('Cancelando...');
  };

  const extractSubtopics = (brief: Brief): string[] => {
    const subtopics: string[] = [];
    brief.outline.forEach((section) => {
      subtopics.push(section.heading);
      if (section.subheadings) {
        subtopics.push(...section.subheadings);
      }
    });
    return subtopics.slice(0, 4);
  };

  const startResearch = async () => {
    if (hasStartedRef.current || isExecutingRef.current) {
      return;
    }

    hasStartedRef.current = true;
    isExecutingRef.current = true;
    cancelledRef.current = false;
    setIsCancelled(false);
    if (setIsProcessing) setIsProcessing(true);

    setShowStartButton(false);
    setIsResearching(true);
    setError('');
    setProgress(0);

    let topicsToResearch: string[] = [];
    if (subtopics && subtopics.length > 0) {
      topicsToResearch = subtopics;
    } else if (brief && brief.outline && brief.outline.length > 0) {
      topicsToResearch = extractSubtopics(brief);
    } else {
      setError('No hay subtemas para investigar. Por favor, completa el briefing primero.');
      setIsResearching(false);
      return;
    }

    const topic = mainTopic || (brief ? brief.mainKeyword : 'Tema principal');

    try {
      setCurrentTopic(`Iniciando investigación con Perplexity...`);
      const researchResults: ResearchResult[] = [];

      for (let i = 0; i < topicsToResearch.length; i++) {
        // Check if cancelled before each iteration
        if (cancelledRef.current) {
          throw new Error('CANCELLED');
        }

        const subtopic = topicsToResearch[i];

        const progressPercent = (i / topicsToResearch.length) * 100;
        setProgress(progressPercent);
        setCurrentTopic(`Investigando "${subtopic}"...`);

        try {
          const result = await webhookService.conductResearch([subtopic], topic, sessionId, articleType);
          if (result && result.length > 0) {
            researchResults.push(result[0]);
          }
          const newProgress = ((i + 1) / topicsToResearch.length) * 100;
          setProgress(newProgress);
        } catch (error) {
          // Check if this was a cancellation
          if (error instanceof Error && error.message === 'CANCELLED') {
            throw error; // Re-throw to be caught by outer catch
          }
          researchResults.push({
            topic: subtopic,
            mainTopic: topic,
            synthesis: 'No se pudo obtener información para este subtema',
            sources: [],
            keyInsights: [],
            error: error instanceof Error ? error.message : 'Error desconocido'
          } as any);
        }

        if (i < topicsToResearch.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      setProgress(100);
      setResults(researchResults);
      setCurrentTopic('¡Investigación completada!');

      if (setIsProcessing) setIsProcessing(false);

      setTimeout(() => {
        onComplete(researchResults);
      }, 1000);
    } catch (error) {
      // Handle cancellation separately
      if (error instanceof Error && error.message === 'CANCELLED') {
        setCurrentTopic('Investigación cancelada');
        setIsResearching(false);
        setShowStartButton(true);
        hasStartedRef.current = false;
        isExecutingRef.current = false;
        if (setIsProcessing) setIsProcessing(false);
        return;
      }

      setError('Hubo un error durante la investigación. Por favor, intenta nuevamente.');
      setIsResearching(false);
      setShowStartButton(true);
      hasStartedRef.current = false;
      isExecutingRef.current = false;
      if (setIsProcessing) setIsProcessing(false);
    } finally {
      isExecutingRef.current = false;
    }
  };

  if (showStartButton && !isResearching) {
    let topicsToShow: string[] = [];
    if (subtopics && subtopics.length > 0) {
      topicsToShow = subtopics;
    } else if (brief && brief.outline && brief.outline.length > 0) {
      topicsToShow = extractSubtopics(brief);
    }

    return (
      <Card className="overflow-hidden border-none shadow-xl bg-gradient-to-br from-white to-blue-50/50">
        <div className="text-center py-10 px-6">
          <div className="relative mb-8 inline-block">
            <div className="absolute inset-0 bg-blue-400 blur-2xl opacity-20 rounded-full animate-pulse"></div>
            <div className="w-24 h-24 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-2xl rotate-3 flex items-center justify-center shadow-2xl relative z-10 transition-transform hover:rotate-6 duration-500">
              <Search className="w-12 h-12 text-white/90 drop-shadow-md" />
            </div>
          </div>

          <h2 className="text-3xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-800">
            Investigación Profunda
          </h2>

          <p className="text-gray-600 mb-8 max-w-lg mx-auto text-lg leading-relaxed">
            Analizaremos <span className="font-bold text-indigo-600">{topicsToShow.length} temas clave</span> utilizando <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-md font-medium border border-indigo-100">Perplexity AI</span> para obtener datos frescos y verificados.
          </p>

          {topicsToShow.length > 0 && (
            <div className="mb-10 text-left bg-white/80 backdrop-blur-sm rounded-2xl border border-indigo-100 shadow-sm overflow-hidden max-w-2xl mx-auto">
              <div className="bg-indigo-50/50 px-6 py-3 border-b border-indigo-100 flex items-center justify-between">
                <span className="text-xs font-bold text-indigo-500 uppercase tracking-wider flex items-center">
                  <BookOpen className="w-3 h-3 mr-1.5" /> PLAN DE INVESTIGACIÓN
                </span>
                <span className="text-xs font-medium text-indigo-400 flex items-center bg-indigo-50 px-2 py-1 rounded-full">
                  <Clock className="w-3 h-3 mr-1" /> ~{(topicsToShow.length * 15)}s
                </span>
              </div>
              <div className="p-2 space-y-1">
                {topicsToShow.map((topic, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 p-3 hover:bg-indigo-50/30 rounded-lg transition-colors group"
                  >
                    <div className="flex-shrink-0 w-8 h-8 bg-white border-2 border-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center text-sm font-bold shadow-sm group-hover:border-indigo-300 group-hover:scale-105 transition-all">
                      {index + 1}
                    </div>
                    <span className="text-gray-700 font-medium group-hover:text-indigo-700 transition-colors line-clamp-1">
                      {topic}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {onBack && (
              <Button
                onClick={onBack}
                variant="outline"
                size="lg"
                className="w-full sm:w-auto px-8 py-6 text-gray-500 hover:text-gray-700 font-medium"
              >
                Volver atrás
              </Button>
            )}

            <Button
              onClick={startResearch}
              size="lg"
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-10 py-6 text-lg rounded-xl shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-1 active:translate-y-0 transition-all font-bold"
            >
              <Sparkles className="w-5 h-5 mr-2 animate-pulse" />
              Comenzar Investigación
            </Button>
          </div>

          <p className="text-xs text-gray-400 mt-6 flex items-center justify-center gap-1">
            <Activity className="w-3 h-3" /> Powered by Advanced AI Agents
          </p>
        </div>
      </Card>
    );
  }

  if (isResearching) {
    let topicsToShow: string[] = [];
    if (subtopics && subtopics.length > 0) {
      topicsToShow = subtopics;
    } else if (brief && brief.outline && brief.outline.length > 0) {
      topicsToShow = extractSubtopics(brief);
    }

    return (
      <Card className="text-center overflow-hidden border-none shadow-xl bg-white">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 animate-gradient-x"></div>
        <div className="py-8 px-4">
          <div className="flex items-center justify-center mb-8 relative">
            <div className="absolute inset-0 bg-indigo-100 rounded-full scale-150 animate-ping opacity-20"></div>
            <div className="w-20 h-20 bg-white border-4 border-indigo-50 rounded-full flex items-center justify-center shadow-lg relative z-10">
              <Search className="w-8 h-8 text-indigo-600 animate-bounce-slow" />
            </div>
          </div>

          <h3 className="text-2xl font-bold mb-2 text-gray-800">Analizando información...</h3>
          <p className="text-indigo-600 mb-8 font-medium animate-pulse">{currentTopic}</p>

          <div className="max-w-xl mx-auto mb-8">
            <ProgressBar value={progress} label="" />
            <div className="flex justify-between items-center mt-2 text-xs text-gray-400 font-medium uppercase tracking-wide">
              <span>Inicio</span>
              <span>{Math.round(progress)}% Completado</span>
            </div>
          </div>

          {topicsToShow.length > 0 && (
            <div className="mt-8 text-left bg-gray-50/50 rounded-xl border border-gray-100 p-4 max-w-2xl mx-auto shadow-inner">
              <div className="grid gap-3">
                {topicsToShow.map((topic, index) => {
                  const isCompleted = progress >= ((index + 1) / topicsToShow.length) * 100;
                  const isInProgress = progress >= (index / topicsToShow.length) * 100 && !isCompleted;

                  return (
                    <div
                      key={index}
                      className={`flex items-center space-x-4 p-3 rounded-lg border transition-all duration-500 ${isCompleted
                        ? 'bg-green-50/50 border-green-200 shadow-sm'
                        : isInProgress
                          ? 'bg-white border-indigo-200 shadow-md ring-2 ring-indigo-50 my-1'
                          : 'bg-transparent border-transparent opacity-50'
                        }`}
                    >
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isCompleted ? 'bg-green-500 text-white' : isInProgress ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'
                        }`}>
                        {isCompleted ? <CheckCircle className="w-5 h-5" /> : isInProgress ? <Activity className="w-4 h-4 animate-spin" /> : <span className="text-xs font-bold">{index + 1}</span>}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium break-words leading-snug transition-colors ${isCompleted ? 'text-green-800' : isInProgress ? 'text-indigo-900' : 'text-gray-500'
                          }`}>
                          {topic}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )
          }

          {/* Cancel Button */}
          <div className="mt-6">
            <Button
              onClick={handleCancel}
              disabled={isCancelled}
              variant="secondary"
              className="bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 px-6 py-2"
            >
              <XCircle className="w-4 h-4 mr-2" />
              {isCancelled ? 'Cancelando...' : 'Cancelar Investigación'}
            </Button>
          </div>
        </div >
      </Card >
    );
  }

  if (error) {
    const handleRetry = () => {
      hasStartedRef.current = false;
      isExecutingRef.current = false;
      setError('');
      setShowStartButton(true);
      setIsResearching(false);
    };

    return (
      <Card className="border-red-100 shadow-lg">
        <Alert type="error" title="Investigación interrumpida">
          {error}
        </Alert>
        <div className="mt-4 flex justify-end">
          <Button onClick={handleRetry} className="bg-red-50 text-red-600 hover:bg-red-100 border border-red-200">
            Intentar nuevamente
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-green-50 to-white border-green-100 shadow-lg">
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6 animate-bounce-short">
          <CheckCircle className="w-10 h-10" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Investigación Completada!</h2>
        <p className="text-gray-600 mb-8 max-w-md">
          Hemos recopilado <span className="font-bold text-green-700">{results.reduce((acc, r) => acc + r.keyInsights.length, 0)} insights clave</span> de fuentes verificadas.
        </p>

        <div className="w-full max-w-2xl grid grid-cols-1 gap-3 text-left">
          {results.map((result, index) => (
            <div key={index} className="bg-white p-4 rounded-lg border border-green-100 shadow-sm flex items-start space-x-3">
              <div className="shrink-0 w-2 h-2 rounded-full bg-green-400 mt-2"></div>
              <div>
                <h3 className="font-medium text-gray-900">{result.topic}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {result.keyInsights.length} puntos de datos extraídos
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};



